function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { Cookies, LocalStorage } from '@stripe-internal/stripe-cookies';
import UAParser from 'ua-parser-js';
import { createRuntimeAdapter, isBrowserPageContext } from './runtimeAdapter';
import { defaultAnalyticsConfig, prodUrl, qaUrl } from './types';
import { isBot } from '../utils/botDetect';
import { generateID, snakeCase, toQueryString } from '../utils/string';
import { injectGoogleAnalytics } from '../utils/googleAnalytics';
import { memoizeForHalfSecond } from '../utils/memoizeForHalfSecond';
import { BatchLogger } from '../utils/batchLogger';
import { deepMerge } from '../utils/deepMerge';
import { cleanPagePath, cleanValues } from '../utils/piiScrubber';
import { warn } from '../utils/warn';
// Internal state for things we've called once.
// This is intentionally shared between all instances of the AnalyticsReporter class.
const ONCE_CACHE = {
    track: {},
    viewed: {},
    modal: {},
    action: {},
    link: {}
};
// Takes a url and fires a request via an Image element.
const injectPixel = (url, onAnalyticsRequestFailed, runtimeAdapter)=>{
    if (runtimeAdapter && !runtimeAdapter.supportsViewport) {
        return;
    }
    try {
        const pxl = new Image();
        pxl.onerror = function() {
            warn('pixel_onerror_event', url);
            onAnalyticsRequestFailed === null || onAnalyticsRequestFailed === void 0 ? void 0 : onAnalyticsRequestFailed('ae-pixel-error', 1);
        };
        pxl.src = url;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e) {
        warn('pixel_thrown_error', e);
        onAnalyticsRequestFailed === null || onAnalyticsRequestFailed === void 0 ? void 0 : onAnalyticsRequestFailed('ae-pixel-throws', 1, e);
    }
};
const SK_REGEX = /(sk|rk)_live_[a-zA-Z0-9]*/gm;
const cleanObjectInner = (// eslint-disable-next-line @typescript-eslint/no-explicit-any
object, onTrackingIssue, depth)=>{
    let newObject = object;
    // set a limit on depth to prevent recursive objects from going on infinitely
    if (newObject !== null && depth < 100) {
        switch(typeof newObject){
            case 'string':
                newObject = newObject.replace(SK_REGEX, '[REDACTED_LIVE_KEY]');
                break;
            case 'object':
                if (newObject instanceof Array) {
                    // Prevent modifying the original array reference
                    newObject = [
                        ...newObject
                    ];
                    const { length } = newObject;
                    for(let i = 0; i < length; i++){
                        newObject[i] = cleanObjectInner(newObject[i], onTrackingIssue, depth + 1);
                    }
                } else {
                    // Prevent modifying the original object reference
                    newObject = _object_spread({}, newObject);
                    for(const i in newObject){
                        if (Object.prototype.hasOwnProperty.call(newObject, i)) {
                            newObject[i] = cleanObjectInner(newObject[i], onTrackingIssue, depth + 1);
                        }
                    }
                }
                break;
            default:
                break;
        }
    } else if (depth >= 100) {
        onTrackingIssue('recursive_object');
    }
    return newObject;
};
const cleanObject = (// eslint-disable-next-line @typescript-eslint/no-explicit-any
object, onTrackingIssue, config)=>{
    const params = cleanObjectInner(object, onTrackingIssue, 0);
    if ('page' in params) {
        params.page = cleanPagePath(`${params.page}`, config.safeQueryParams);
    }
    if ('referer' in params) {
        params.referer = cleanPagePath(`${params.referer}`, config.safeQueryParams);
    }
    if ('referrer' in params) {
        params.referrer = cleanPagePath(`${params.referrer}`, config.safeQueryParams);
    }
    if ('from_url' in params) {
        params.from_url = cleanPagePath(`${params.from_url}`, config.safeQueryParams);
    }
    return params;
};
// This returns the path if we're missing a name for the page
export const getBackupViewedName = (eventName, lastViewedEventName, config, runtimeAdapter)=>{
    // If the user didn't pass in a viewed_name, but we saved the last call
    // to `Analytics.viewed`. So we can just use that value with some
    // confidence.
    if (lastViewedEventName) {
        return lastViewedEventName;
    } else {
        // In this case, we don't have any `viewed_name` information to send
        // along with the request. Warn, but don't lose the event entirely.
        warn('event_with_no_viewed', eventName);
        // Lets send the URL path when we don't have the `viewed_name` so
        // we can identify where its missing and fix those cases
        let pagePath = runtimeAdapter ? runtimeAdapter.getPagePath() : null;
        if (!runtimeAdapter && !pagePath && isBrowserPageContext()) {
            pagePath = document.location.pathname;
        }
        return pagePath ? cleanPagePath(pagePath, config === null || config === void 0 ? void 0 : config.safeQueryParams) : 'unknown';
    }
};
const ua = new UAParser().getResult();
const userAgentParams = {
    ua_browser_name: ua.browser.name,
    ua_browser_version: ua.browser.version,
    ua_os_name: ua.os.name,
    ua_os_version: ua.os.version,
    ua_device_model: ua.device.model,
    ua_device_type: ua.device.type,
    ua_device_vendor: ua.device.vendor,
    ua_engine_name: ua.engine.name,
    ua_engine_version: ua.engine.version
};
Object.entries(userAgentParams).forEach(([key, value])=>{
    if (!value) {
        // Delete undefined values so they are not reported as "undefined" strings
        delete userAgentParams[key];
    }
});
// This returns global parameters which are generated for each event.
export const getGlobalParams = (config, clientIDs, runtimeAdapter, sessionID)=>{
    const params = _object_spread({
        cid: clientIDs.cid,
        lsid: clientIDs.lsid,
        analytics_ua: 'analytics.js-CURRENT_VERSION'
    }, userAgentParams);
    const pagePath = runtimeAdapter.getPagePath();
    if (pagePath) {
        const page = config.maxPageLength ? pagePath.slice(0, config.maxPageLength) : pagePath;
        params.page = cleanPagePath(page, config.safeQueryParams);
    }
    const referrer = runtimeAdapter.getReferrer();
    if (referrer != null) {
        params.referrer = cleanPagePath(referrer, config.safeQueryParams);
    }
    const domain = runtimeAdapter.getDomain();
    if (domain) {
        params.domain = domain;
    }
    const viewport = runtimeAdapter.getViewport();
    if (viewport) {
        params.viewport_height = viewport.height;
        params.viewport_width = viewport.width;
    }
    if (config.sessionization && sessionID) {
        params.sid = sessionID;
    }
    if (config.botDetection && isBot()) {
        // Only add if true to reduce payload size for non-bot traffic
        params.bot = true;
    }
    return params;
};
// We store cachedClientIDs as a singleton that can be shared across multiple AnalyticsReporter instances.
// This avoids race conditions during async I/O to cookies/localStorage when using multiple reporters. If
// you need unique IDs per client, disable set the 'memoizeCookieID' config to 'false'.
let cachedClientIDs = null;
let cachedSessionID = null;
let cookieSetPromiseInFlight = false;
let temporarySessionID = null;
export class AnalyticsReporter {
    configure(config) {
        if (config.googleAnalytics && this.runtimeAdapter.supportsPageContext) {
            injectGoogleAnalytics(config);
        }
        // Since we have some nested objects here use recursive merge to prevent overriding already set objects
        this.config = deepMerge(this.config, config);
        if (this.config.inProduction === undefined) {
            this.config.inProduction = true;
        }
        if (config.namespace) {
            this.prefix = config.namespace;
        }
        if (this.config.useCookies === 'newCookies' && this.runtimeAdapter.supportsCookies) {
            return this.setConfigCookieData();
        } else {
            return Promise.resolve();
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deprecatedSetConfigCookieData() {
        var _this = this;
        return _async_to_generator(function*() {
            if (!_this.cookies) {
                return;
            }
            // Which config parameters to append to the cookie
            const allowlistedCookieParams = [
                'merchant',
                'user'
            ];
            const { cookies } = _this;
            return Promise.all(allowlistedCookieParams.map((k)=>{
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'AnalyticsConfig'.
                const value = _this.config[k];
                if (typeof value === 'string') {
                    return cookies.set(k, value);
                } else {
                    return null;
                }
            })).catch((e)=>{
                // Similar to the comment above, let's see where we are failing
                warn('unable_to_set_cookie_data', e);
            });
        })();
    }
    setConfigCookieData() {
        var _this = this;
        return _async_to_generator(function*() {
            if (!_this.cookies) {
                return;
            }
            const { cookies } = _this;
            try {
                if (_this.config.cookies) {
                    if (_this.config.cookies.merchant && typeof _this.config.cookies.merchant === 'string') {
                        const host = _this.runtimeAdapter.getDomain();
                        const domain = host && `.${host}`.endsWith('.stripe.com') ? '.stripe.com' : undefined;
                        yield cookies.set('merchant', _this.config.cookies.merchant, {
                            domain,
                            secure: true
                        });
                    }
                    if (_this.config.cookies.user && typeof _this.config.cookies.user === 'string') {
                        yield cookies.set('user', _this.config.cookies.user);
                    }
                }
            } catch (e) {
                warn('unable_to_set_cookie_data', e);
            }
        })();
    }
    /**
   * Method to retrieve the unique event ID from the most recently fired event.
   * Used for when we need to push the event ID into other sources to correate
   * event and action.
   */ getPreviousEventId() {
        // If there isn't a pageviewtoken already set, this was called before
        // an analytics event was fired on this page
        let { pageviewtoken } = this.config;
        if (!pageviewtoken) {
            pageviewtoken = generateID();
            this.config.pageviewtoken = pageviewtoken;
            warn('no_previous_event_id');
        }
        // Unique id of the previous analytics event
        return `${pageviewtoken}_${this.state.trackCounter}`;
    }
    /**
   * Call `trackOnce` as many times as you want, but it will only actually send
   * events once. Passes the first set of values through to `track` and ignores
   * all the rest.
   */ trackOnce(eventName, params = {}, options = {}, onTrackingIssue) {
        if (!ONCE_CACHE.track[eventName]) {
            ONCE_CACHE.track[eventName] = true;
            return this.track(eventName, params, options, undefined, onTrackingIssue);
        }
    }
    /**
   * The `track` method is the generic analytics method. It should only
   * be used when none of the other more specific tracking methods do not
   * fit your use case. This is so events can be more easily compared across
   * implementations. Feel free to ask if there's a "best practice" for your
   * unique event type.
   */ track(eventName, params = {}, options = {}, doubleSnakeCase = true, // eslint-disable-next-line @typescript-eslint/no-empty-function
    onTrackingIssue = ()=>{}) {
        var _this_config_cookies, _this_config_cookies1;
        // The event param is the primary event name.
        // Only track() needs to add the prefix as all the other
        // functions call track() and we don't want duplicate prefixes
        const formattedEventName = doubleSnakeCase ? snakeCase(eventName) : eventName;
        const prefixedEventName = this.prefix ? `${this.prefix}.${formattedEventName}` : formattedEventName;
        params.event = prefixedEventName;
        // Bump the count before we set it on the params
        this.state.trackCounter += 1;
        // Number of events fired on this page_view_id
        params['event_count'] = this.state.trackCounter;
        // Set the page view id of the view
        if (this.config.pageviewtoken) {
            params['page_view_id'] = this.config.pageviewtoken;
        } else {
            // Set a uuid for page_view_id if not provided
            const uuid = generateID();
            params['page_view_id'] = uuid;
            this.config.pageviewtoken = uuid;
        }
        // Unique id of the event
        if (typeof params.page_view_id === 'string') {
            params['event_id'] = `${params['page_view_id']}_${this.state.trackCounter}`;
        }
        var _this_config_cookies_merchant;
        // Stripe user information
        const merchant = (_this_config_cookies_merchant = (_this_config_cookies = this.config.cookies) === null || _this_config_cookies === void 0 ? void 0 : _this_config_cookies.merchant) !== null && _this_config_cookies_merchant !== void 0 ? _this_config_cookies_merchant : null;
        var _this_config_cookies_user;
        const user = (_this_config_cookies_user = (_this_config_cookies1 = this.config.cookies) === null || _this_config_cookies1 === void 0 ? void 0 : _this_config_cookies1.user) !== null && _this_config_cookies_user !== void 0 ? _this_config_cookies_user : null;
        if (options.merchant || merchant) {
            params['merchant_id'] = options.merchant || merchant;
        }
        if (options.user || user) {
            if (!params['merchant_id']) {
                warn('missing_merchant_token', prefixedEventName);
            }
            params['user_id'] = options.user || user;
        }
        // Set the app version globally
        // TODO: allow local overrides? unsure how, but seems
        // weird to not allow for some reason?
        if (!params.version && (options.version || this.config.version)) {
            params.version = options.version || this.config.version;
        }
        // Set the flags using the this.config, but merge in local overrides
        if (params.flags || this.config.googleAnalyticsFlags) {
            // @ts-expect-error - TS2698 - Spread types may only be created from object types.
            params.flags = _object_spread({}, this.config.googleAnalyticsFlags, params.flags);
        }
        // TODO: this is a good example of a thing that could change
        // mid-app usage, based on user interaction, and so could imply
        // that we should allow calling configure again, and just "mix-in"
        // the new options. But it's unclear if that causes weirder state
        // that's unexpected, and that we should force end-developers to
        // maintain their ideal config state.
        if (options.locale || this.config.locale) {
            params['stripe_locale'] = options.locale || this.config.locale;
        }
        const setCookieConsent = Promise.resolve().then(()=>{
            if (!this.cookies) {
                params['privacy_consent_loaded'] = false;
                params['privacy_consent_functional'] = false;
                params['privacy_consent_advertising'] = false;
                return;
            }
            params['privacy_consent_loaded'] = true;
            const advertisingCheck = this._getPrivacyConsentAdvertising().then((privacyConsentAdvertising)=>{
                params['privacy_consent_advertising'] = privacyConsentAdvertising;
            });
            const functionalCheck = this._getPrivacyConsentFunctional().then((privacyConsentFunctional)=>{
                params['privacy_consent_functional'] = privacyConsentFunctional;
            });
            return Promise.all([
                advertisingCheck,
                functionalCheck
            ]);
        });
        // Grab our global parameters
        return Promise.all([
            setCookieConsent,
            this.getClientIDs(),
            this.getSessionID()
        ]).then(([_cookieConsent, clientIDs, sessionID])=>{
            const globalParams = getGlobalParams(this.config, clientIDs, this.runtimeAdapter, sessionID);
            // Merge the local and global params, preferring the local ones for overrides
            // Flow throws a `cannot-spread-indexer` error if we try to spread here
            const mergedParams = _object_spread({}, globalParams, params);
            const cleanedParams = cleanObject(mergedParams, onTrackingIssue, this.config);
            if (!this.config.overrideTrackingUrl) {
                var _this_config_batchSettings;
                // Send post request to AEL
                if ((_this_config_batchSettings = this.config.batchSettings) === null || _this_config_batchSettings === void 0 ? void 0 : _this_config_batchSettings.enableBatching) {
                    this._postBatchAnalyticsEventLogger(_object_spread({}, cleanedParams));
                } else {
                    this._postAnalyticsEventLogger(_object_spread({}, cleanedParams), options.sendIframeMessage);
                }
            } else {
                // Send event to endpoint using pixel injection method
                // TODO: remove these when deleting legacy analytics code to keep event
                // slim enough to fit in nginx log
                delete cleanedParams.performance_events;
                delete cleanedParams.treatments;
                // Generate the query string based on the set of parameters
                const qs = `?${toQueryString(cleanedParams)}`;
                const trackingUrl = (this.config.inProduction ? this.config.overrideTrackingUrl.prod : this.config.overrideTrackingUrl.qa) + qs;
                if (this.config.inProduction) {
                    // Call the tracking function, which is usually the pixel injection.
                    this.trackingFunctions.forEach((fn)=>{
                        fn(trackingUrl, this.config.onAnalyticsRequestFailed, this.runtimeAdapter);
                    });
                }
            }
            this.extendSessionExpiration();
            return cleanedParams;
        });
    }
    /**
   * Call `viewedOnce` as many times as you want, but it will only actually send
   * events once. Passes the first set of values through to `viewed` and ignores
   * all the rest.
   */ viewedOnce(viewedName, params = {}, options = {}) {
        if (!ONCE_CACHE.viewed[viewedName]) {
            ONCE_CACHE.viewed[viewedName] = true;
            return this.viewed(viewedName, params, options);
        }
    }
    /**
   * `viewed` calls are made on pageload, or javascript history change, or
   * similar instance where a predominantly new set of content is now being
   * shown to users.
   */ viewed(pageName, params = {}, options = {}) {
        // Clean up the event name
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const eventName = snakeCase(pageName);
        // Add the additional `viewed_name` param, which is expected with this
        // type of event
        // TODO: is it ever possible to have a user pass in a `viewed_name` here
        // that *doesn't* match the pageName?
        params['viewed_name'] = eventName;
        // Remember the most recent page that was viewed, for adding context
        // to other requests.
        this.state.lastViewedEventName = eventName;
        // Pass along the event to the generic `track` function
        return this.track(`${eventName}.viewed`, params, options, /* doubleSnakeCase */ false);
    }
    /**
   * Call `modalOnce` as many times as you want, but it will only actually send
   * events once. Passes the first set of values through to `modal` and ignores
   * all the rest.
   */ modalOnce(modalName, params = {}, options = {}) {
        if (!ONCE_CACHE.modal[modalName]) {
            ONCE_CACHE.modal[modalName] = true;
            return this.modal(modalName, params, options);
        }
    }
    /**
   * `modal` events are to be fired when a modal is opened. It is required
   * that a `viewed` call has been made prior to calling any `modal` call,
   * since this is important contextual information.
   */ modal(modalName, params = {}, options = {}) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const eventName = snakeCase(modalName);
        params['modal_name'] = eventName;
        // Set the viewed_name based on the params or else the last stored viewed_name
        if (!params['viewed_name']) {
            params['viewed_name'] = getBackupViewedName(modalName, this.state.lastViewedEventName, this.config, this.runtimeAdapter);
        }
        this.state.lastModalEventName = eventName;
        return this.track(`${eventName}.modal`, params, options, /* doubleSnakeCase */ false);
    }
    /**
   * Call `actionOnce` as many times as you want, but it will only actually send
   * events once. Passes the first set of values through to `action` and ignores
   * all the rest.
   */ actionOnce(actionName, params = {}, options = {}) {
        if (!ONCE_CACHE.action[actionName]) {
            ONCE_CACHE.action[actionName] = true;
            return this.action(actionName, params, options);
        }
    }
    /**
   * `action` calls are made when the user interacts with the page. Button
   * clicks are very common use cases. It is required that a `viewed` call
   * has been made, and if applicable, that a `modal` call has been made if
   * the action is taking place inside of a modal.
   */ action(actionName, params = {}, options = {}) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const eventName = snakeCase(actionName);
        params['action_name'] = eventName;
        // Set the viewed_name based on the params or else the last stored viewed_name
        if (!params['viewed_name']) {
            params['viewed_name'] = getBackupViewedName(actionName, this.state.lastViewedEventName, this.config, this.runtimeAdapter);
        }
        // Because we cannot tell if a modal was dismissed prior to the action,
        // we expect the implementation to include a modalName in the params instead
        // of using the stored modal name.
        return this.track(`${eventName}.action`, params, options, /* doubleSnakeCase */ false);
    }
    /**
   * Call `linkOnce` as many times as you want, but it will only actually send
   * events once. Passes the first set of values through to `link` and ignores
   * all the rest.
   */ linkOnce(linkName, params = {}, options = {}) {
        if (!ONCE_CACHE.link[linkName]) {
            ONCE_CACHE.link[linkName] = true;
            return this.link(linkName, params, options);
        }
    }
    /**
   * Link events are fired when a link leaves the current scope of the domain
   */ link(linkName, params = {}, options = {}) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const eventName = snakeCase(linkName);
        params['link_name'] = eventName;
        // Set the viewed_name based on the params or else the last stored viewed_name
        if (!params['viewed_name']) {
            params['viewed_name'] = getBackupViewedName(linkName, this.state.lastViewedEventName, this.config, this.runtimeAdapter);
        }
        return this.track(`${eventName}.link`, params, options, /* doubleSnakeCase */ false);
    }
    /**
   * Get analytics ID from cookie or localStorage, else mint one.
   * This memoizes _getClientIds().
   */ getClientIDs() {
        var _this = this;
        return _async_to_generator(function*() {
            if (!_this.config.memoizeCookieID) {
                return _this._getClientIDs();
            }
            // Ensure that we don't try to refetch client IDs (or fetch multiple
            // concurrently if there are more than one on the page, which can cause
            // a race condition):
            if (cachedClientIDs === null) {
                cachedClientIDs = _this._getClientIDs();
            }
            return cachedClientIDs;
        })();
    }
    getSessionID() {
        var _this = this;
        return _async_to_generator(function*() {
            if (!_this.config.sessionization) {
                return '';
            }
            const sessionId = cachedSessionID || _this._getSessionID();
            return sessionId;
        })();
    }
    /**
   * This takes the mergedParams from Analytics.track and sends a POST request to the
   * Analytics Event Logger service at r.stripe.com/0.
   */ _postAnalyticsEventLogger(params, sendIframeMessage) {
        var _this = this;
        return _async_to_generator(function*() {
            const trackingUrl = _this.config.inProduction ? _this._namedUrls.prod : _this._namedUrls.qa;
            if (_this.config.clientId) {
                params.client_id = _this.config.clientId;
            } else {
                warn('no_client_id');
                return;
            }
            _this.cleanAelParams(params);
            const body = toQueryString(params);
            if (sendIframeMessage) {
                sendIframeMessage({
                    body
                });
            } else {
                try {
                    const result = yield fetch(trackingUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        },
                        body,
                        credentials: 'omit'
                    });
                    if (!result.ok) {
                        var _this_config_onAnalyticsRequestFailed, _this_config;
                        (_this_config_onAnalyticsRequestFailed = (_this_config = _this.config).onAnalyticsRequestFailed) === null || _this_config_onAnalyticsRequestFailed === void 0 ? void 0 : _this_config_onAnalyticsRequestFailed.call(_this_config, 'ael-single-error', 1);
                    }
                    return result;
                } catch (err) {
                    var _this_config_onAnalyticsRequestFailed1, _this_config1;
                    (_this_config_onAnalyticsRequestFailed1 = (_this_config1 = _this.config).onAnalyticsRequestFailed) === null || _this_config_onAnalyticsRequestFailed1 === void 0 ? void 0 : _this_config_onAnalyticsRequestFailed1.call(_this_config1, 'ael-single-throws', 1, err);
                }
            }
        })();
    }
    /**
   * This takes the mergedParams from Analytics.track and adds the event to a
   * buffer, to be sent in a batched POST request to r.stripe.com/b.
   */ _postBatchAnalyticsEventLogger(params) {
        if (params.client_id) {
            delete params.client_id;
        }
        this.cleanAelParams(params);
        if (this.batchLogger) {
            this.batchLogger.pushEvent(_object_spread({}, params));
        } else {
            warn('batching_disabled');
        }
    }
    constructor(config = defaultAnalyticsConfig){
        var _config_batchSettings;
        _define_property(this, "config", void 0);
        _define_property(this, "runtimeAdapter", void 0);
        _define_property(this, "cookies", void 0);
        _define_property(this, "localStorage", void 0);
        _define_property(this, "state", void 0);
        _define_property(this, "trackingFunctions", void 0);
        _define_property(this, "_namedUrls", {
            prod: prodUrl,
            qa: qaUrl
        });
        _define_property(this, "prefix", void 0);
        _define_property(this, "batchLogger", void 0);
        _define_property(this, "_extendSessionExpirationPromise", void 0);
        var _this = this;
        _define_property(this, "_getPrivacyConsentAdvertising", memoizeForHalfSecond(/*#__PURE__*/ _async_to_generator(function*() {
            var _this1;
            if (!((_this1 = _this) === null || _this1 === void 0 ? void 0 : _this1.cookies)) {
                throw new Error('Cookies must be defined at this point');
            }
            return _this.cookies.isCategoryAllowed('advertising');
        })));
        var _this1 = this;
        _define_property(this, "_getPrivacyConsentFunctional", memoizeForHalfSecond(/*#__PURE__*/ _async_to_generator(function*() {
            var _this;
            if (!((_this = _this1) === null || _this === void 0 ? void 0 : _this.cookies)) {
                throw new Error('Cookies must be defined at this point');
            }
            const cookieAllowed = yield _this1.cookies.isCategoryAllowed('functional');
            return cookieAllowed;
        })));
        var _this2 = this;
        _define_property(this, "_getClientIDs", memoizeForHalfSecond(/*#__PURE__*/ _async_to_generator(function*() {
            if (!_this2.cookies || !_this2.localStorage) {
                return {
                    cid: '',
                    lsid: ''
                };
            }
            const cid = _this2.cookies.get('cid');
            const lsid = _this2.localStorage.get('lsid');
            // The common case: both values are present
            if (cid && lsid) {
                return Promise.resolve({
                    cid,
                    lsid
                });
            } else if (cid && !lsid) {
                // We just have a cookie, so set the localstorage.
                return _this2.localStorage.set('lsid', cid).then(()=>({
                        cid,
                        lsid: cid
                    }));
            } else if (lsid && !cid) {
                // We just have a localstorage value, so set the cookie.
                return _this2.cookies.set('cid', lsid).then(()=>({
                        lsid,
                        cid: lsid
                    }));
            }
            // If we're here, there were no IDs to be found and reused
            const id = generateID();
            // TODO: in the past, if both cookies and localstorage threw errors, I
            // think this might have returned 'NA', but it seems like even single
            // page sessions, in memory, would be more useful than that.
            return Promise.all([
                _this2.cookies.set('cid', id),
                _this2.localStorage.set('lsid', id)
            ]).then(()=>({
                    cid: id,
                    lsid: id
                }));
        })));
        var _this3 = this;
        /**
   * Get session ID from cookie or generate a new one.
   * This function memoizes the session ID in memory only if cookies cannot be set given enforcement mode.
   */ _define_property(this, "_getSessionID", memoizeForHalfSecond(/*#__PURE__*/ _async_to_generator(function*() {
            if (!_this3.cookies) {
                return '';
            }
            const sid = _this3.cookies.get('__Secure-sid');
            if (sid) {
                return sid;
            }
            const id = generateID();
            /**
     * There's a situation where even the half second memoization is not enough. If the function is called at least half a second apart
     * but creating the cookie has not yet resolved.
     * To handle this, we check if the cookie setting promise is already in flight and return a temporary session id if that's the case.
     * This is unlikely to happen, but will prevent an edge case where 0 second sessions were created with single events.
     */ if (cookieSetPromiseInFlight) {
                return temporarySessionID || id;
            }
            // Set temporary ID for concurrent calls and mark that we're setting the cookie
            temporarySessionID = id;
            cookieSetPromiseInFlight = true;
            try {
                const couldSetCookie = yield _this3.cookies.set('__Secure-sid', id, {
                    domain: _this3.config.sessionizationDomain
                });
                if (couldSetCookie) {
                    return id;
                }
                // Cookie setting failed: cache the ID for future calls within the same page view (hard reload)
                if (!cachedSessionID) {
                    cachedSessionID = Promise.resolve(id);
                }
                return cachedSessionID;
            } finally{
                // Cleanup in memory state.
                cookieSetPromiseInFlight = false;
                temporarySessionID = null;
            }
        })));
        var _this4 = this;
        _define_property(this, "extendSessionExpiration", memoizeForHalfSecond(/*#__PURE__*/ _async_to_generator(function*() {
            if (_this4.config.sessionization && _this4.cookies) {
                const sid = yield _this4.getSessionID();
                _this4.cookies.set('__Secure-sid', sid, {
                    domain: _this4.config.sessionizationDomain
                });
            }
        })));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        _define_property(this, "cleanAelParams", (params)=>{
            // some slight renaming for AEL endpoints' schema.
            params.event_name = params.event;
            delete params.event;
            params.created = new Date().getTime() / 1000;
            cleanValues(params, this.config.safeParams);
            return params;
        });
        const { stripeCookiesEnforcementMode: enforcementMode } = config;
        this.config = _object_spread({}, defaultAnalyticsConfig, config);
        this.runtimeAdapter = createRuntimeAdapter(this.config);
        this.state = {
            trackCounter: 0
        };
        this.trackingFunctions = [
            injectPixel
        ];
        var _config_namespace;
        this.prefix = (_config_namespace = config.namespace) !== null && _config_namespace !== void 0 ? _config_namespace : '';
        if ((_config_batchSettings = config.batchSettings) === null || _config_batchSettings === void 0 ? void 0 : _config_batchSettings.enableBatching) {
            this.batchLogger = new BatchLogger(this.config);
        }
        if (config.useCookies !== 'disabled' && this.runtimeAdapter.supportsCookies) {
            this.cookies = new Cookies({
                enforcementMode
            });
            this.localStorage = new LocalStorage({
                enforcementMode
            });
        }
        this._extendSessionExpirationPromise = null;
        this.configure(this.config);
    }
}

//# sourceMappingURL=AnalyticsReporter.js.map